import React, { useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { buildScript } from './narration';
import { BRAND, INTRO_TEXT, OUTRO_TEXT } from './brandContent';

const grey = { color: '6B7280', size: 18 };

export const buildDoc = async () => {
  const { Document, Packer, Paragraph, TextRun, HeadingLevel, PageBreak } = await import('docx');
  const P = (text, opts = {}) => new Paragraph({ spacing: { after: 120 }, ...opts, children: [new TextRun({ text, size: 22, ...(opts.run || {}) })] });
  const H = (text, heading) => new Paragraph({ text, heading, spacing: { before: 240, after: 120 } });
  const modules = buildScript();
  const total = modules.reduce((a, m) => a + m.workflows.reduce((b, w) => b + w.secs, 0), 0);
  const children = [
    new Paragraph({ heading: HeadingLevel.TITLE, children: [new TextRun({ text: 'AudiNexa — Feature Video Voice-over Script' })] }),
    P(`Demo clinic: Hear All, Bengaluru · ${modules.reduce((a, m) => a + m.workflows.length, 0)} workflows · approx. ${Math.round(total / 60)} minutes of narration`, { run: grey }),
    P('Each workflow lists the screenshot it belongs to, an estimated read time at a measured pace (about 140 words per minute) and the voice-over text. Module overviews can open each chapter of the video.', { run: grey }),
  ];
  children.push(H('00.0 · Intro', HeadingLevel.HEADING_1), P('Slide: animated AudiNexa intro   ·   Audio: 00.0-intro.mp3', { run: grey }), P(INTRO_TEXT));
  modules.forEach((m) => {
    children.push(new Paragraph({ children: [new PageBreak()] }));
    children.push(H(`${m.no} · ${m.title}`, HeadingLevel.HEADING_1));
    children.push(P(`Screenshot: ${m.overviewShot}   ·   Audio: ${m.no}.0-${m.slug}.mp3`, { run: grey }));
    children.push(P(m.overview, { run: { italics: true } }));
    m.workflows.forEach((w) => {
      children.push(H(`${w.ref}  ${w.title}`, HeadingLevel.HEADING_2));
      children.push(P(`Screenshot: ${w.shotName}   ·   Audio: ${w.shotName.replace('.png', '.mp3')}   ·   approx. ${w.secs} sec`, { run: grey }));
      children.push(P(w.text));
    });
  });
  children.push(new Paragraph({ children: [new PageBreak()] }), H('99.0 · Outro — Book a free demo', HeadingLevel.HEADING_1),
    P(`Slide: animated outro with ${BRAND.website} · ${BRAND.phone} · ${BRAND.email}   ·   Audio: 99.0-outro.mp3`, { run: grey }),
    P(OUTRO_TEXT));
  const doc = new Document({
    creator: 'AudiNexa', title: 'AudiNexa Voice-over Script',
    styles: { default: { document: { run: { font: 'Calibri' } } } },
    sections: [{ properties: {}, children }],
  });
  return Packer.toBlob(doc);
};

export const ScriptDownloadButton = () => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const blob = await buildDoc();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'AudiNexa-Voiceover-Script.docx';
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      toast.success('Voice-over script downloaded');
    } catch (e) {
      toast.error('Could not build the script');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className="sc-btn sc-btn-ghost" onClick={download} disabled={busy} data-testid="showcase-script-download-btn">
      {busy ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
      Download voice-over script
    </button>
  );
};
